import { z } from "zod";

/**
 * Phase 2B-Mengen (Pfad B) — Output der in-process Vektor-Takeoff-Engine
 * (@bidi-handel/takeoff `runMengenTakeoff`). Ersetzt den alten chained-CLI-
 * Pfad. Pro Plan-PDF ein Eintrag mit Maßstab, Räumen, abgeleiteten Mengen und
 * den Roh-Polygonen (GeoJSON) als editierbare Quelle für den MengenTab-Editor.
 *
 * Mengen sind PROVISORISCH: aus der deterministischen Topologie-Engine, ohne
 * Öffnungs-Abzug, vor der Editor-Korrektur. Das daraus abgeleitete
 * `02-vor-lv-positionen.json` trägt `provisional: true`.
 */

const Din277Schema = z.object({
  main: z.enum(["NUF", "VF", "TF", "KF"]),
  sub: z.string(),
  name: z.string(),
});

const PointSchema = z.object({ x: z.number(), y: z.number() });

/**
 * Material-Referenz einer Raum-Bemusterung (P4 „LV-Text-Light"). Entweder
 * Freitext ODER ein direkt gewählter Artikel (Code + Bezeichnung) — fließt in
 * den `originaltext`/`kurzText` der synthetisierten vor-LV-Position ein und
 * verbessert so den Match-Pre-Filter (Pfad B hat sonst keine LV-Texte).
 */
export const MaterialRefSchema = z.object({
  freitext: z.string().optional(),
  artikelCode: z.string().optional(),
  artikelBezeichnung: z.string().optional(),
});
export type MaterialRef = z.infer<typeof MaterialRefSchema>;

/**
 * Manuell gezeichnete Wand (P2c) — einzelner Strich mit optionalen Material-
 * Metadaten. Wenn ein Raum Wände trägt, leitet `recomputeMengen` die Wandfläche
 * aus der Summe der Strich-Längen × Raumhöhe ab (statt Umfang × Raumhöhe).
 */
export const MengenWallSchema = z.object({
  id: z.string(),
  start: PointSchema,
  end: PointSchema,
  gewerk: z
    .enum(["rohbau", "trockenbau", "putz", "oberflaeche", "sonstiges"])
    .optional(),
  dickeMm: z.number().positive().optional(),
  materialName: z.string().optional(),
  /**
   * Direkt zugewiesener Konstruktions-/Set-Artikel (#9). Wird im MengenTab pro
   * gezeichneter Wand gewählt und treibt den `vorausgewaehlterArtikelCode` der
   * daraus synthetisierten Konstruktions-Position — Phase 4 expandiert die
   * Stückliste dieses Artikels zur Schnellkalkulation (Vorrang vor dem
   * generischen Wand-Code-Template).
   */
  artikelCode: z.string().optional(),
  artikelBezeichnung: z.string().optional(),
});
export type MengenWall = z.infer<typeof MengenWallSchema>;

export const MengenRoomSchema = z.object({
  roomId: z.string(),
  label: z.string(),
  din277: Din277Schema,
  bodenflaecheM2: z.number(),
  umfangM: z.number(),
  raumhoeheM: z.number(),
  wandflaecheM2: z.number(),
  isInsideBuilding: z.boolean(),
  /**
   * Quelle der Bodenfläche (P1): `polygon` = aus dem gezeichneten Polygon
   * (Shoelace); `ngf-stempel` = aus dem im Plan gestempelten NGF-Wert (Vorrang);
   * `manuell` = vom Innendienst gesetzt. `recomputeMengen` zieht die Geometrie
   * (Umfang/Wand) IMMER aus dem Polygon nach, überschreibt `bodenflaecheM2` aber
   * nur bei `polygon` — Stempel-/Manuell-Werte bleiben erhalten.
   * Default `polygon` hält bestehende 02-mengen.json abwärtskompatibel.
   */
  quelleBoden: z.enum(["polygon", "ngf-stempel", "manuell"]).default("polygon"),
  /** Roher NGF-Stempelwert (m²), falls ein 1:1-Stempel zugeordnet wurde. */
  ngfStempelM2: z.number().optional(),
  /** Drift Stempel↔aktuelle Polygonfläche (Stempel/Polygon); ~1 = Übereinstimmung. */
  ngfDrift: z.number().optional(),
  /** Raum-Bemusterung (P4): Material je Bauteil → reichert die vor-LV-Texte an. */
  bemusterung: z
    .object({
      boden: MaterialRefSchema.optional(),
      wand: MaterialRefSchema.optional(),
      decke: MaterialRefSchema.optional(),
    })
    .optional(),
  /** Manuell gezeichnete Wand-Striche (P2c). Wenn gesetzt+nicht leer, bestimmt
   *  ihre Summen-Länge die Wandfläche (statt Umfang × Raumhöhe). */
  walls: z.array(MengenWallSchema).optional(),
  /**
   * Wohneinheit-Zuordnung (#5A) — freier Tag wie „WE 1" / „WHG 3", den der
   * Innendienst im MengenTab vergibt (Multi-Select) oder per Plan-Stempel
   * vorbelegt. Die Menge der distinkten Werte IST die WE-Liste; leer/undefined
   * = keiner Einheit zugeordnet. Rein organisatorisch — beeinflusst in 5A weder
   * Mengen noch vor-LV (Downstream-Gliederung folgt in 5B).
   */
  wohneinheit: z.string().optional(),
});
export type MengenRoom = z.infer<typeof MengenRoomSchema>;

/**
 * GeoJSON-Polygone (PDF-Pt-Koords) als editierbare Quelle für den MengenTab.
 * Nur die editor-relevanten Felder werden gespeichert (id + Ring-Koordinaten +
 * Fläche/Umfang/Wand-Refs); Engine-Diagnostik und Material/Öffnungen werden beim
 * Schreiben verworfen (die Engine ist die Source of Truth, der Editor braucht sie
 * für Slice 1 nicht). KEIN passthrough — sonst kollidiert die zod-Index-Signatur
 * mit dem Engine-Interface PolygonBuilderOutput.
 */
export const MengenPolygonsSchema = z.object({
  type: z.literal("FeatureCollection"),
  features: z.array(
    z.object({
      type: z.literal("Feature"),
      geometry: z.object({
        type: z.literal("Polygon"),
        coordinates: z.array(z.array(z.array(z.number()))),
      }),
      properties: z.object({
        id: z.string(),
        areaM2: z.number(),
        perimeterM: z.number(),
        wallSegmentRefs: z.array(z.string()).optional(),
        closureConfidence: z.number().optional(),
      }),
    })
  ),
});

/** Eine Schicht der mehrschaligen Außenwand (Verblender/Dämmung/Innenschale). */
export const AussenwandSchichtSchema = z.object({
  key: z.string(),
  thicknessM: z.number(),
  flaecheM2: z.number(),
  volumenM3: z.number(),
  stueck: z.number().optional(),
  stkEinheit: z.string().optional(),
});
export type AussenwandSchicht = z.infer<typeof AussenwandSchichtSchema>;

/** Außenwand-Schichten-Mengen je Plan (mehrschalige Allplan-Außenwand). */
export const AussenwandSchema = z.object({
  laufLaengeM: z.number(),
  /** `footprint` = aus Gebäude-Außenkontur (Raum-Union, erfasst L-Formen),
   *  `bbox` = Bounding-Box-Umfang (Fallback). */
  laufLaengeQuelle: z.enum(["footprint", "bbox"]).default("bbox"),
  wandHoeheM: z.number(),
  schichten: z.array(AussenwandSchichtSchema).default([]),
});
export type Aussenwand = z.infer<typeof AussenwandSchema>;

export const MengenPlanSchema = z.object({
  /** Relativer Pfad des Plan-PDFs (aus eingang/). */
  sourceFile: z.string(),
  pageIndex: z.number().int().nonnegative(),
  rasterMode: z.enum(["vector", "raster", "mixed"]),
  scale: z.object({
    ratio: z.number(),
    source: z.string(),
  }),
  rooms: z.array(MengenRoomSchema).default([]),
  /** Außenwand-Schichten-Mengen (optional; nur wenn Schalen vermessbar). */
  aussenwand: AussenwandSchema.optional(),
  polygons: MengenPolygonsSchema,
  warnings: z.array(z.string()).default([]),
});
export type MengenPlan = z.infer<typeof MengenPlanSchema>;

export const MengenSchema = z.object({
  schemaVersion: z.literal(1).default(1),
  pfad: z.literal("B"),
  status: z.enum(["ok", "failed", "partial"]).default("ok"),
  begruendung: z.string().optional(),
  /** Default-Raumhöhe (m), mit der die Wandflächen abgeleitet wurden. */
  raumhoeheM: z.number(),
  plaene: z.array(MengenPlanSchema).default([]),
});
export type Mengen = z.infer<typeof MengenSchema>;
